// Capa de capacidades por canal -- FASE 1 de "capacidades por canal",
// SOLO la capa, sin aplicar a ninguna pantalla todavía (ver TAREA).
//
// Resuelve el problema de fondo: un if por botón por canal se vuelve
// inmanejable con 5 canales y una docena de funciones, y el próximo canal
// que se agregue rompe todos los ifs que ya existían. Acá se declara UNA
// vez, por canal, qué soporta -- el resto del código pregunta, nunca
// vuelve a listar canales.
//
// ── Por qué un objeto plano, no una función ─────────────────────────────
// Se pidió explícitamente que el archivo "declare" -- un objeto es dato,
// se lee de un vistazo, se audita en code review como una tabla, y un
// canal nuevo es agregar una fila, no escribir lógica nueva. Una función
// por canal escondería la tabla real detrás de código, y agregar un canal
// implicaría escribir un nuevo `case` en vez de copiar una fila y ajustar
// valores -- exactamente el patrón de "un if por botón" que esto viene a
// reemplazar, solo que centralizado en un solo lugar en vez de repartido
// en 12 pantallas.
//
// ── Por qué integracionesExternas no rompe el patrón ────────────────────
// Cloud API soporta Typebot y nada más (confirmado en el inventario:
// ProcessWhatsappCloudApiWebhookService.ts tiene un chequeo explícito que
// ignora cualquier integración que no sea `type === 'typebot'`). En vez
// de inventar un campo aparte solo para esta capacidad (que sí sería un
// caso especial), CUALQUIER capacidad de esta tabla puede valer:
//   - true           -- soportada sin condiciones
//   - false           -- no soportada
//   - string[]        -- soportada SOLO para esos sub-valores
// canalSoporta() entiende las tres formas de manera genérica -- no hay
// una rama de código dedicada a integracionesExternas, el helper ni sabe
// que esa capacidad es la única que hoy usa la forma array. Si mañana
// "plantillas" también necesita restringirse a un subconjunto, ya
// funciona, sin tocar el helper.
//
// Fuente de cada valor: inventario de canales (Fase 0) + verificación de
// impacto en producción (Fase de impacto) de esta misma tarea. Donde la
// evidencia es indirecta (facebook/instagram, que no eran el foco del
// inventario original) se marca con comentario -- no se inventó ningún
// valor sin algo que lo respalde en el código ya leído.
export const CAPACIDADES_POR_CANAL = {
  // Baileys -- protocolo no oficial, sin restricciones de Meta. Todo lo
  // que existe en el CRM para WhatsApp se construyó pensando en este
  // canal primero; es el único con soporte completo.
  whatsapp: {
    texto: true,
    plantillas: false, // Baileys no tiene el concepto de plantilla pre-aprobada -- manda texto libre siempre, no hace falta.
    medios: true,
    audio: true,
    stickers: true,
    reenviar: true,
    editar: true,
    borrar: true,
    reacciones: true,
    encuestas: true,
    botones: true,
    listas: true,
    flujos: true,
    frasesAutomaticas: true,
    chatbotNativo: true,
    campanas: true,
    integracionesExternas: true, // cualquier tipo (Typebot, DialogFlow, n8n, webhook, SGP).
  },

  // WhatsApp Oficial -- API oficial servida por un BSP externo (no Meta
  // directo). Cubre texto/medios/plantillas, pero las acciones que
  // dependen de la API cruda de Baileys (editar, borrar, reacciones,
  // encuestas, botones/listas interactivos) truenan en tiempo de
  // ejecución: GetTicketWbot/getWbot devuelven `undefined` para este
  // canal (no lanzan, así que el error real aparece un paso después, al
  // intentar usar ese `undefined` como socket).
  whatsapp_oficial: {
    texto: true,
    plantillas: true,
    medios: true,
    audio: true,
    stickers: true,
    reenviar: true,
    editar: false,
    borrar: false,
    reacciones: false,
    encuestas: false,
    botones: false,
    listas: false,
    flujos: true,
    frasesAutomaticas: false, // el matching de frase (FlowCampaignModel) vive solo en wbotMessageListener.ts (Baileys) -- cero referencias en el lado oficial.
    chatbotNativo: true,
    campanas: false, // handleDispatchCampaign no tiene rama para este canal -- GetWhatsappWbot devuelve undefined, el job hace `logger.error` y sale, sin marcar el envío como fallido.
    integracionesExternas: true, // sin la restricción a Typebot que sí tiene Cloud API -- VerifyQueue.ts no filtra por tipo.
  },

  // Meta Cloud API directa. Construida para texto y plantillas -- todo lo
  // demás fue quedando explícitamente fuera de alcance ("fica fora do
  // escopo do MVP de App Review", comentario real en el código) y nunca
  // se retomó. La mayoría de estos `false` son fallas SILENCIOSAS, no
  // errores -- ver el inventario para el detalle de cada una.
  whatsapp_cloud_api: {
    texto: true,
    plantillas: true,
    medios: true,
    audio: true,
    stickers: true,
    reenviar: false, // ni siquiera hay una rama vacía -- forwardMessage no contempla este canal en absoluto.
    editar: false,
    borrar: false,
    reacciones: false,
    encuestas: false,
    botones: false,
    listas: false,
    flujos: false, // cero ocurrencias de "whatsapp_cloud_api" en ActionsWebhookService.ts -- ningún nodo de flujo, ni uno de texto plano, hace nada en este canal.
    frasesAutomaticas: false,
    chatbotNativo: false, // no existe un listener de chatbot para este canal (comparar con ChatBotListener.ts / ChatBotListenerOficial.ts).
    campanas: false, // mismo mecanismo silencioso que whatsapp_oficial (GetWhatsappWbot -> undefined -> logger.error y sale) -- confirmado en producción, sin campañas nuevas desde el cambio de canal para no arriesgarlo en vivo.
    integracionesExternas: ["typebot"], // único tipo soportado -- confirmado por el chequeo explícito en ProcessWhatsappCloudApiWebhookService.ts que ignora cualquier otro tipo con un console.log.
  },

  // Facebook Messenger. No era el foco del inventario original (el pedido
  // fue Baileys vs Cloud API) -- estos valores salen de releer el mismo
  // código ya revisado, no de una investigación nueva y dedicada. Menor
  // confianza que las tres filas de arriba; conviene una pasada aparte
  // antes de confiar en esta fila para ocultar algo en pantalla.
  facebook: {
    texto: true,
    plantillas: false,
    medios: true,
    audio: true,
    stickers: false, // sin evidencia de que sendFacebookMessageMedia soporte stickers -- tratado como no soportado hasta confirmar, no como "sí funciona".
    reenviar: false, // forwardMessage solo contempla whatsapp/whatsapp_oficial, ningún otro canal.
    editar: false,
    borrar: false,
    reacciones: false,
    encuestas: false,
    botones: false,
    listas: false,
    flujos: false, // cero ocurrencias de "facebook"/"instagram" en ActionsWebhookService.ts.
    frasesAutomaticas: false,
    chatbotNativo: true, // existe ChatbotListenerFacebook.ts, con su propio flujo de bot.
    campanas: false, // sin evidencia de soporte -- valor conservador, no confirmado explícitamente.
    integracionesExternas: false, // sin evidencia de soporte -- valor conservador.
  },

  // Instagram Direct. En todo el código ya revisado, instagram viaja
  // SIEMPRE junto a facebook en el mismo chequeo (`["facebook",
  // "instagram"].includes(channel)`) -- nunca se los trata distinto. Por
  // eso esta fila es una copia exacta de "facebook", no una fila
  // investigada por separado. Mismo aviso: confirmar antes de usarla para
  // ocultar algo en pantalla.
  instagram: {
    texto: true,
    plantillas: false,
    medios: true,
    audio: true,
    stickers: false,
    reenviar: false,
    editar: false,
    borrar: false,
    reacciones: false,
    encuestas: false,
    botones: false,
    listas: false,
    flujos: false,
    frasesAutomaticas: false,
    chatbotNativo: true,
    campanas: false,
    integracionesExternas: false,
  },
};

// canalSoporta(channel, capacidad, opciones?) -- decide por UN canal
// puntual. Es lo que se usa para decidir por TICKET (`ticket.channel`),
// que es el caso que obliga a esta capa a existir: una empresa puede
// tener Cloud API para recibir y Baileys por QR para cerrar ventas a
// mano -- el mismo agente ve el botón de adjuntar en un ticket sí, en el
// otro no, según el canal DE ESE ticket, no de la empresa entera.
//
// `opciones.subValor` -- para capacidades cuyo valor es un array (hoy
// solo integracionesExternas): qué sub-tipo puntual se necesita, ej.
// canalSoporta('whatsapp_cloud_api', 'integracionesExternas', { subValor: 'dialogflow' })
// -- false, porque Cloud API solo tiene 'typebot' en su lista. Sin
// `subValor`, un array se interpreta como "soporta AL MENOS un tipo".
//
// `opciones.overrideAll` -- ver más abajo (punto 5, superadmin).
export function canalSoporta(channel, capacidad, opciones = {}) {
  const { subValor, overrideAll = false } = opciones;
  if (overrideAll) return true;

  const tabla = CAPACIDADES_POR_CANAL[channel];
  if (!tabla) return false; // canal desconocido -- conservador, no asumir soporte.

  const valor = tabla[capacidad];
  if (valor === undefined) return false; // capacidad no declarada -- mismo criterio.

  if (typeof valor === "boolean") return valor;

  if (Array.isArray(valor)) {
    if (!subValor) return valor.length > 0;
    return valor.includes(subValor);
  }

  return false;
}

// empresaSoporta(conexiones, capacidad, opciones?) -- true si CUALQUIER
// conexión de la empresa soporta la capacidad. Para decidir el MENÚ (ej.
// si el ítem de Frases automáticas aparece en el sidebar) -- ahí no hay
// un ticket puntual todavía, así que se pregunta a nivel empresa: si
// tiene al menos una conexión capaz, se muestra la entrada; la decisión
// fina de si tal ticket puntual puede usarla vive en canalSoporta().
//
// `conexiones` acepta el array de conexiones tal cual lo entrega
// WhatsAppsContext (objetos con `.channel`) o, para tests/usos sueltos,
// un array plano de strings de canal -- por eso el `c?.channel ?? c`.
//
// `opciones` se reenvía tal cual a canalSoporta() en cada conexión -- eso
// incluye `subValor` (ej. empresaSoporta(conexiones, 'integracionesExternas',
// { subValor: 'typebot' }) para saber si ALGUNA conexión soporta Typebot
// puntual, no solo integraciones en general) y `overrideAll` (superadmin,
// ver el hook). No hay dos implementaciones del mismo chequeo -- este
// helper solo decide "alguna conexión", canalSoporta() decide "esta
// conexión puntual", con las mismas opciones en los dos.
export function empresaSoporta(conexiones, capacidad, opciones = {}) {
  const { overrideAll = false } = opciones;
  if (overrideAll) return true;
  if (!Array.isArray(conexiones) || conexiones.length === 0) return false;

  return conexiones.some((c) => canalSoporta(c?.channel ?? c, capacidad, opciones));
}
